import { useEffect, useState } from 'react'
import { usePostHog } from 'posthog-js/react'
import { useTimeout } from 'usehooks-ts'

import { useAuthState } from '@percent/admin-dashboard/common/hooks/useAuthState/useAuthState'
import { FEATURE_FLAGS_READY_DEADLINE_MS } from '@percent/admin-dashboard/constants/routeGuardTiming'

/*
  Whether PostHog has finished loading feature flags for the *current* user.

  When PostHog is not configured (e.g. local dev without an API key) flags are
  treated as ready so callers fall back to their default behaviour, mirroring
  Split's offline mode.

  When a user is logged in, readiness additionally requires that PostHog has
  identify()-ed them and (re)loaded flags for that identified distinct id. On a
  hard page load PostHog first loads flags for the anonymous bootstrap user and
  only identify()s the logged-in user once auth state is restored (see
  PostHogProvider). Treating the anonymous flag set as "ready" let route guards
  (FeatureRoute / AccessRoute) fail closed and redirect to the default route
  before identify() completed — see DORA-1151.
*/
export function useFeatureFlagsReady(): boolean {
  const posthog = usePostHog()
  const { user } = useAuthState()
  const [flagsDistinctId, setFlagsDistinctId] = useState<string | null>(null)
  const [deadlineReached, setDeadlineReached] = useState(false)

  useEffect(() => {
    if (!posthog) {
      return undefined
    }

    // Record the distinct id in effect at the time of each flags load so we can
    // tell an identified flag set apart from the anonymous bootstrap set.
    return posthog.onFeatureFlags(() => setFlagsDistinctId(posthog.get_distinct_id?.() ?? null))
  }, [posthog])

  useTimeout(() => setDeadlineReached(true), FEATURE_FLAGS_READY_DEADLINE_MS)

  // eslint-disable-next-line no-underscore-dangle -- `__loaded` is PostHog's internal "SDK initialised" flag
  const isConfigured = !!posthog && posthog.__loaded === true

  if (!isConfigured) {
    return true
  }

  // Never block the UI indefinitely if the identified flag set never arrives.
  if (deadlineReached) {
    return true
  }

  const hasLoadedFlags = Boolean(posthog?.featureFlags?.hasLoadedFlags)

  // A logged-in user is only "ready" once the most recent flags load was
  // evaluated for their identified distinct id, not the anonymous user.
  if (user?.id) {
    return hasLoadedFlags && flagsDistinctId === user.id
  }

  return hasLoadedFlags
}
